
/*eslint-disable*/
import React from "react";
import classNames from "classnames";
import { NavLink, useLocation, useNavigate, matchPath } from "react-router-dom";
import PropTypes from "prop-types";
// javascript plugin used to create scrollbars on windows
import PerfectScrollbar from "perfect-scrollbar";

// reactstrap components
import { Nav, Collapse, Button } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHouse } from "@fortawesome/free-regular-svg-icons";
import { MdOutlineCircle } from "react-icons/md";
import { IoIosArrowDown } from "react-icons/io";
import { RiSidebarFoldLine, RiSidebarUnfoldLine } from "react-icons/ri";
import { adminNavbarAppearanceShared } from "../Navbars/adminNavbarAppearanceShared";
import { isWebMakerRouteBlocked } from "utils/permissionUtils";
var ps;

const Sidebar = (props) => {
  const [state, setState] = React.useState({});
  const sidebarRef = React.useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [isMobile, setIsMobile] = React.useState(window.innerWidth < 992);

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const effectiveSidebarMini = props.sidebarMini && !isMobile;

  const shellThemeVariant = props.shellThemeVariant === "2" ? "2" : "1";
  const isTheme2MiniSidebar = shellThemeVariant === "2" && effectiveSidebarMini;
  const theme2CollapseStateBeforeMiniRef = React.useRef(null);
  const theme2MobileLogoIconOnly = shellThemeVariant === "2" && isMobile;
  const shouldExpandTheme2Sidebar = shellThemeVariant === "2" && props.sidebarMini && !isMobile;

  const handleTheme2SidebarClickCapture = React.useCallback((event) => {
    if (!shouldExpandTheme2Sidebar || typeof props.handleMiniClick !== "function") {
      return;
    }

    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }

    const interactiveElement = target.closest("a, button, [role='button']");
    if (interactiveElement && sidebarRef.current?.contains(interactiveElement)) {
      if (
        interactiveElement.closest("[data-sidebar-route-link='true']") ||
        interactiveElement.closest("[data-sidebar-collapse-trigger='true']")
      ) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      props.handleMiniClick();
    }
  }, [props.handleMiniClick, shouldExpandTheme2Sidebar]);

  const pathnameMatchesSidebarPattern = (pathname, routePattern) => {
    if (!routePattern) return false;
    if (routePattern.includes(":")) {
      return Boolean(matchPath({ path: routePattern, end: true }, pathname));
    }
    if (pathname === routePattern) return true;
    return pathname.startsWith(`${routePattern}/`);
  };

  /** Tam path, alt rota veya sidebarActiveWhenPaths (örn. /inventory/42 → Search Inventory) */
  const isSidebarRouteActive = (routePath, activeWhenPaths = []) => {
    if (!routePath) return false;
    const { pathname } = location;
    if (pathnameMatchesSidebarPattern(pathname, routePath)) return true;
    return (activeWhenPaths || []).some((pattern) =>
      pathnameMatchesSidebarPattern(pathname, pattern)
    );
  };

  const isSidebarRouteExactActive = (routePath) => {
    if (!routePath) return false;
    return location.pathname === routePath;
  };

  const activeRouteClass = (routePath, activeWhenPaths) =>
    isSidebarRouteActive(routePath, activeWhenPaths) ? "active" : "";

  const handleRouteClick = (routePath) => (event) => {
    if (shellThemeVariant !== "2") {
      if (typeof props.closeSidebar === "function") {
        props.closeSidebar();
      }
      return;
    }

    event.preventDefault();

    if (shouldExpandTheme2Sidebar) {
      event.stopPropagation();
      if (typeof props.handleMiniClick === "function") {
        props.handleMiniClick();
      }
      if (!isSidebarRouteExactActive(routePath)) {
        navigate(routePath);
      }
      return;
    }

    if (isSidebarRouteExactActive(routePath)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (typeof props.closeSidebar === "function") {
      props.closeSidebar();
    }
    navigate(routePath);
  };

  React.useEffect(() => {
    if (isTheme2MiniSidebar) {
      setState(getClosedCollapseStates(props.routes));
      return;
    }
    setState(getCollapseStates(props.routes));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount: route tabanlı ilk collapse durumu
  }, []);

  React.useEffect(() => {
    if (shellThemeVariant !== "2") {
      return;
    }

    if (effectiveSidebarMini) {
      setState((prevState) => {
        theme2CollapseStateBeforeMiniRef.current = Object.keys(prevState).reduce(
          (acc, key) => (prevState[key] ? { ...acc, [key]: true } : acc),
          {}
        );
        return { ...prevState, ...getClosedCollapseStates(props.routes) };
      });
      return;
    }

    if (!theme2CollapseStateBeforeMiniRef.current) {
      return;
    }

    const savedOpen = theme2CollapseStateBeforeMiniRef.current;
    theme2CollapseStateBeforeMiniRef.current = null;

    setState((prevState) => {
      const next = { ...prevState, ...savedOpen };
      const applyActiveRouteCollapses = (routes) => {
        routes.forEach((route) => {
          if (route.collapse && route.views) {
            if (getCollapseInitialState(route.views)) {
              next[route.state] = true;
            }
            applyActiveRouteCollapses(route.views);
          }
        });
      };
      applyActiveRouteCollapses(props.routes);
      return next;
    });
  }, [props.routes, effectiveSidebarMini, shellThemeVariant]);

  React.useEffect(() => {
    if (shellThemeVariant !== "2" || effectiveSidebarMini) {
      return;
    }

    let activeCollapseState = null;
    const findActiveCollapseState = (routes) => {
      routes.forEach((route) => {
        if (route.collapse && route.views) {
          if (getCollapseInitialState(route.views)) {
            activeCollapseState = route.state;
          }
          findActiveCollapseState(route.views);
        }
      });
    };

    findActiveCollapseState(props.routes);

    if (!activeCollapseState) {
      return;
    }

    setState((prevState) => ({
      ...prevState,
      [activeCollapseState]: true,
    }));
  }, [location.pathname, props.routes, shellThemeVariant, effectiveSidebarMini]);

  React.useEffect(() => {
    /* Tema 2: native overflow-y scroll (tüm platformlar). Tema 1: Windows’ta PerfectScrollbar. */
    if (shellThemeVariant === "2") {
      return undefined;
    }
    if (navigator.platform.indexOf("Win") > -1 && sidebarRef.current) {
      ps = new PerfectScrollbar(sidebarRef.current, {
        suppressScrollX: true,
        suppressScrollY: false,
      });
    }
    return function cleanup() {
      if (navigator.platform.indexOf("Win") > -1 && ps) {
        try {
          ps.destroy();
        } catch (e) {
          /* noop */
        }
      }
    };
  }, [shellThemeVariant]);
  // this creates the intial state of this component based on the collapse routes
  // that it gets through props.routes
  const getCollapseStates = (routes) => {
    let initialState = {};
    routes.map((prop, key) => {
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };
  const getClosedCollapseStates = (routes) =>
    Object.keys(getCollapseStates(routes)).reduce(
      (acc, collapseStateKey) => ({
        ...acc,
        [collapseStateKey]: false,
      }),
      {}
    );
  // this verifies if any of the collapses should be default opened on a rerender of this component
  // for example, on the refresh of the page,
  // while on the src/views/forms/RegularForms.js - route /admin/regular-forms
  const getCollapseInitialState = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
        return true;
      } else if (
        isSidebarRouteActive(routes[i].path, routes[i].sidebarActiveWhenPaths)
      ) {
        return true;
      }
    }
    return false;
  };
  const { account } = props;
  // this function creates the links and collapses that appear in the sidebar (left menu)
  const renderCollapseCaret = () => {
    if (shellThemeVariant === "2") {
      return (
        <span className="caret sidebar-theme2-collapse-caret">
          <IoIosArrowDown size={14} aria-hidden />
        </span>
      );
    }
    return <b className="caret" />;
  };

  const renderMiniIconOrPlaceholder = (miniIconProp) => {
    if (miniIconProp) {
      if (
        shellThemeVariant === "2" &&
        React.isValidElement(miniIconProp) &&
        typeof miniIconProp.props?.size === "number"
      ) {
        return React.cloneElement(miniIconProp, {
          size: miniIconProp.props.size + 2,
        });
      }
      return miniIconProp;
    }
    return <MdOutlineCircle size={shellThemeVariant === "2" ? 14 : 12} />;
  };

  const renderIcon = (icon) => {
    if (!icon) {
      return null;
    }

    let element = null;
    if (React.isValidElement(icon)) {
      if (shellThemeVariant === "2") {
        const s = icon.props?.size;
        element =
          typeof s === "number"
            ? React.cloneElement(icon, { size: s + 2 })
            : React.cloneElement(icon, { size: 22 });
      } else {
        element = icon.props.size ? icon : React.cloneElement(icon, { size: 20 });
      }
    } else if (typeof icon === "function") {
      element = React.createElement(icon, {
        size: shellThemeVariant === "2" ? 22 : 20,
      });
    } else if (typeof icon === "string") {
      element = <i className={icon} />;
    }

    if (!element) {
      return null;
    }

    const iconMarginLeft =
      shellThemeVariant === "2" ? 0 : effectiveSidebarMini ? 15 : 0;

    return (
      <span className="sidebar-icon" style={{ marginLeft: iconMarginLeft }}>
        {element}
      </span>
    );
  };

  /** Tema 2: /dashboard satırında regular “house” (Font Awesome). */
  const renderRouteIcon = (prop) => {
    if (shellThemeVariant === "2" && prop.path === "/dashboard" && prop.icon !== undefined) {
      return (
        <span className="sidebar-icon sidebar-shell-theme-2-home-icon" style={{ marginLeft: 0 }}>
          <FontAwesomeIcon
            icon={faHouse}
            style={{ fontSize: 18 }}
          />
        </span>
      );
    }
    const resolvedIcon =
      shellThemeVariant === "2" && prop.iconTheme2 !== undefined ? prop.iconTheme2 : prop.icon;
    return renderIcon(resolvedIcon);
  };

  const createLinks = (routes) => {
    return routes
      .filter((prop) => !prop.invisible)
      .map((prop, key) => {
        if (prop.redirect) {
          return null;
        }
        if (prop.superAdminOnly && !account?.is_super_admin) {
          return null;
        }
        if (prop.permission && account && !account.is_super_admin && account.permissions && !account.permissions[prop.permission]) {
          return null;
        }

        // Tenant web_enabled overrides web_maker permission
        if (isWebMakerRouteBlocked(account, prop)) {
          return null;
        }

        if (prop.hideForSuperAdmin && account && account.is_super_admin) {
          return null;
        }

        if (
          prop.requiresCommunicationsView &&
          account &&
          !account.is_super_admin &&
          !account.can_communications_view
        ) {
          return null;
        }

        // Report access control
        if (prop.state === "reportsCollapse" && account) {
          const hasReportsRead = account.permissions && account.permissions.reports_read;

          if (!hasReportsRead) {
            return null;
          }
        }

        if (prop.collapse) {
          var st = {};
          st[prop["state"]] = !state[prop.state];
          return (
            <li
              className={getCollapseInitialState(prop.views) ? "active" : ""}
              key={key}
            >
              <a
                href="#pablo"
                data-toggle="collapse"
                data-sidebar-collapse-trigger="true"

                aria-expanded={state[prop.state]}
                onClick={(e) => {
                  e.preventDefault();
                  if (shouldExpandTheme2Sidebar) {
                    e.stopPropagation();
                    if (typeof props.handleMiniClick === "function") {
                      props.handleMiniClick();
                    }
                    setState((prevState) => ({
                      ...prevState,
                      [prop.state]: true,
                    }));
                    return;
                  }
                  if (shellThemeVariant === "2") {
                    setState((prevState) => ({
                      ...prevState,
                      [prop.state]: !prevState[prop.state],
                    }));
                    return;
                  }
                  setState({ ...state, ...st });
                }}
              >
                {prop.icon !== undefined ? (
                  <>
                    {renderRouteIcon(prop)}
                    <p className="sidebar-normal">
                      {prop.name}
                      {renderCollapseCaret()}
                    </p>
                  </>
                ) : (
                  <>
                    {effectiveSidebarMini &&
                      <span className="sidebar-mini-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {renderMiniIconOrPlaceholder(prop.miniIcon)}
                      </span>
                    }
                    <span className="sidebar-normal">
                      {prop.name}
                      {renderCollapseCaret()}
                    </span>
                  </>
                )}
              </a>
              {shellThemeVariant === "2" ? (
                state[prop.state] && !isTheme2MiniSidebar ? (
                  <div className="sidebar-theme2-submenu">
                    <ul className="nav">{createLinks(prop.views)}</ul>
                  </div>
                ) : null
              ) : (
                <Collapse isOpen={state[prop.state]}>
                  <ul className="nav">{createLinks(prop.views)}</ul>
                </Collapse>
              )}
            </li>
          );
        }
        return (
          <li className={activeRouteClass(prop.path, prop.sidebarActiveWhenPaths)} key={key}>
            <NavLink
              to={prop.path}
              data-sidebar-route-link="true"
              onClick={handleRouteClick(prop.path)}
            >
              {prop.icon !== undefined ? (
                <>
                  {renderRouteIcon(prop)}
                  <p className="sidebar-normal">{prop.name}</p>
                </>
              ) : (
                <>
                  {effectiveSidebarMini &&
                    <span className="sidebar-mini-icon" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {renderMiniIconOrPlaceholder(prop.miniIcon)}
                    </span>
                  }
                  {!effectiveSidebarMini &&
                    <span className="sidebar-normal">
                      {prop.name}
                    </span>
                  }
                </>
              )}
            </NavLink>
          </li>
        );
      });
  };
  const { activeColor, logo } = props;
  const sidebarBrandImgSrc =
    shellThemeVariant === "2" && logo?.imgSrc
      ? `${process.env.PUBLIC_URL}/logo2.svg`
      : logo?.imgSrc;
  let logoImg = null;
  let logoText = null;
  if (logo !== undefined) {
    if (logo.outterLink !== undefined) {
      if (shellThemeVariant === "2") {
        logoImg = (
          <div className="navbar-minimize d-inline">
            <NavLink
              to={logo.outterLink}
              className="d-inline-flex align-items-center sidebar-shell-theme-2-logo-link"
              data-sidebar-route-link="true"
              onClick={handleRouteClick(logo.outterLink)}
            >
              <img
                src={sidebarBrandImgSrc}
                alt={logo.text || "DealertIQ"}
                style={{
                  filter: 'invert(1) brightness(2)',
                  width: effectiveSidebarMini ? '45px' : '50px',
                  cursor: 'pointer'
                }}
              />
            </NavLink>
          </div>
        );
        logoText =
          logo.text && !theme2MobileLogoIconOnly ? (
            <NavLink
              to={logo.outterLink}
              className="simple-text logo-normal sidebar-shell-theme-2-brand-link"
              data-sidebar-route-link="true"
              onClick={handleRouteClick(logo.outterLink)}
            >
              {logo.text}
            </NavLink>
          ) : null;
      } else {
        logoImg = (
          <div className="navbar-minimize d-inline">
            <Button
              className="minimize-sidebar btn-just-icon"
              color="link"
              id="tooltip209599"
              onClick={props.handleMiniClick}
              style={{ marginLeft: '-1%' }}
            >
              <RiSidebarFoldLine size={25} color="white" className="visible-on-sidebar-regular" />
              <RiSidebarUnfoldLine size={25} color="white" className="visible-on-sidebar-mini" />

            </Button>
            {/* <UncontrolledTooltip
          delay={0}
          target="tooltip209599"
          placement="right"
        >
          Sidebar toggle
        </UncontrolledTooltip> */}
          </div>
        );
        logoText = (
          <div className="simple-text logo-normal" style={{ marginLeft: '4rem', fontWeight: '700' }}>
          </div>
        );
      }
    } else {
      logoImg = (
        <NavLink
          to={logo.innerLink}
          className="simple-text logo-mini"
          onClick={props.closeSidebar}
        >
          <div className="logo-img">
            <img src={sidebarBrandImgSrc} alt="react-logo" style={{ filter: 'invert(1) brightness(2)' }} />
          </div>
        </NavLink>
      );
      logoText = (
        <NavLink
          to={logo.innerLink}
          className="simple-text logo-normal"
          onClick={props.closeSidebar}
        >
          {logo.text}
        </NavLink>
      );
    }
  }

  return (
    <div
      className={classNames("sidebar", {
        "sidebar-shell-theme-2": shellThemeVariant === "2",
      })}
      data={activeColor}
    >
      {shellThemeVariant === "2" ? (
        <div className="sidebar-wrapper">
          <div
            className="sidebar-theme2-scroll"
            ref={sidebarRef}
            onClickCapture={handleTheme2SidebarClickCapture}
          >
            {logoImg !== null || logoText !== null ? (
              <div
                className={classNames("logo", {
                  "sidebar-shell-theme-2-logo--mobile-icon-only":
                    theme2MobileLogoIconOnly,
                })}
              >
                {logoImg}
                {logoText}
              </div>
            ) : null}
            <Nav>{createLinks(props.routes)}</Nav>
          </div>
        </div>
      ) : (
        <div className="sidebar-wrapper" ref={sidebarRef}>
          {logoImg !== null || logoText !== null ? (
            <div className="logo">
              {logoImg}
              {logoText}
            </div>
          ) : null}
          <Nav>{createLinks(props.routes)}</Nav>
        </div>
      )}
    </div>
  );
};

Sidebar.propTypes = {
  activeColor: PropTypes.oneOf(["primary", "blue", "green", "orange", "red"]),
  routes: PropTypes.array.isRequired,
  logo: PropTypes.oneOfType([
    PropTypes.shape({
      innerLink: PropTypes.string.isRequired,
      imgSrc: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
    PropTypes.shape({
      outterLink: PropTypes.string.isRequired,
      imgSrc: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ]),
  // this is used on responsive to close the sidebar on route navigation
  closeSidebar: PropTypes.func,
  // this is used to handle demize/maximize
  handleMiniClick: PropTypes.func,
  sidebarMini: PropTypes.bool,
  sidebarMiniLayoutReady: PropTypes.bool,
};

export default Sidebar;
